import { ColumnDef } from '@tanstack/react-table';
import DataTableRowActions from "@/components/data-table/data-table-row-actions.tsx";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header.tsx";
import { Mentee } from "@/types/Mentee.ts";

const EMPTY_LABEL = "Not specified";

function orEmpty(value: string | null | undefined, emptyLabel: string = EMPTY_LABEL): string {
    const s = value != null ? String(value).trim() : "";
    if (s === "" || s.toLowerCase() === "null") return emptyLabel;
    return s;
}

interface MenteesColumnsProps {
    /** If true, show "View/Edit Preference"; if false, show "View Mentorship Group" (after mentor selection deadline). */
    showPreferenceBeforeDeadline: boolean;
    /** Label for null/empty cells (e.g. translated "Not specified"). */
    emptyCellLabel?: string;
}

export const getMenteesColumns = ({
    showPreferenceBeforeDeadline,
    emptyCellLabel = EMPTY_LABEL,
}: MenteesColumnsProps): ColumnDef<Mentee>[] => [
        {
            id: "name",
            accessorFn: row => `${row.user.firstName} ${row.user.lastName}`,
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="name" />
            ),
            enableHiding: false,
        },
        {
            id: "email",
            accessorFn: row => row.user.email,
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="email" />
            ),
        },
        {
            id: "studentStatus",
            accessorFn: (row) => orEmpty(row.studentStatus, emptyCellLabel),
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="studentStatus" />
            ),
            filterFn: (row, id, value) => {
                return String(value) === row.getValue(id);
            },
        },
        {
            id: "disciplines",
            accessorFn: (row) => orEmpty(row.discipline, emptyCellLabel),
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="disciplines" />
            ),
            filterFn: (row, id, value) => {
                return String(value) === row.getValue(id);
            },
        },
        {
            id: "institution",
            accessorFn: (row) => orEmpty(row.institution, emptyCellLabel),
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="institution" />
            ),
            filterFn: (row, id, value) => {
                return String(value) === row.getValue(id);
            },
        },
        {
            id: "region",
            accessorFn: (row) => orEmpty(row.region, emptyCellLabel),
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="region" />
            ),
            filterFn: (row, id, value) => {
                return String(value) === row.getValue(id);
            },
        },
        {
            id: "submittedPreference",
            accessorFn: row => row.hasPreference ? 'Yes' : 'No',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="submittedPreference" />
            ),
            filterFn: (row, id, value) => {
                return String(value) === row.getValue(id);
            },
            enableSorting: false,
        },
        {
            id: "selectedMentor",
            accessorFn: row => (row.mentor != null || row.mentorId != null) ? 'Yes' : 'No',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="selectedMentor" />
            ),
            filterFn: (row, id, value) => {
                return String(value) === row.getValue(id);
            },
            enableSorting: false,
        },
        {
            id: 'actions',
            cell: ({ row }) => {
                const mentee = row.original;
                return (
                    <DataTableRowActions
                        row={row}
                        viewProfileUrl={`/mentees/${mentee.id}`}
                        viewPreferenceUrl={showPreferenceBeforeDeadline ? `/mentees/${mentee.id}/preference` : undefined}
                        viewMentorshipGroupUrl={!showPreferenceBeforeDeadline && mentee.mentor ? `/mentors/${mentee.mentor.id}/mentees` : undefined}
                    />
                );
            },
        },
    ];
